// useMasteryHeroPrefs — UI-only preferences for the home page mastery
// rotator. Lives in localStorage so it survives reloads without
// requiring a sign-in (the home is anon-friendly).
//
// Stores two things:
//   • rotateSpeedMs — how fast the trio of skills rotates. Choices are
//     deliberately limited to 2s / 4s / 6s so the picker stays
//     opinionated (anything faster than 2s is jarring; anything slower
//     than 6s feels static).
//   • roadmapOrder — the user's custom ordering of the 100-skill path,
//     stored as an array of skill `num` values. When present, the hero
//     rotator follows THIS order instead of the canonical shuffle so
//     the visitor literally maps out the voyage they want to take.
//
// Both fields are optional — when missing, the hero falls back to its
// defaults (4s + canonical shuffle), so brand-new visitors get the
// curated experience and only power users see their own ordering.
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hl.mastery.hero.prefs.v1";

export const ROTATE_SPEED_OPTIONS: { ms: number; label: string }[] = [
  { ms: 2000, label: "Fast (2s)" },
  { ms: 4000, label: "Default (4s)" },
  { ms: 6000, label: "Slow (6s)" },
];

export type MasteryHeroPrefs = {
  rotateSpeedMs: number;
  /** Ordered list of skill `num` values. null = use canonical shuffle. */
  roadmapOrder: number[] | null;
  updatedAt: number;
};

const DEFAULTS: MasteryHeroPrefs = {
  rotateSpeedMs: 4000,
  roadmapOrder: null,
  updatedAt: 0,
};

function read(): MasteryHeroPrefs {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<MasteryHeroPrefs>;
    const allowedSpeeds = ROTATE_SPEED_OPTIONS.map((o) => o.ms);
    return {
      rotateSpeedMs:
        typeof parsed.rotateSpeedMs === "number" &&
        allowedSpeeds.includes(parsed.rotateSpeedMs)
          ? parsed.rotateSpeedMs
          : DEFAULTS.rotateSpeedMs,
      roadmapOrder: Array.isArray(parsed.roadmapOrder)
        ? parsed.roadmapOrder.filter((n) => typeof n === "number")
        : null,
      updatedAt: typeof parsed.updatedAt === "number" ? parsed.updatedAt : 0,
    };
  } catch {
    return DEFAULTS;
  }
}

function write(p: MasteryHeroPrefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* quota / privacy mode — silently no-op */
  }
}

export function useMasteryHeroPrefs() {
  const [prefs, setPrefs] = useState<MasteryHeroPrefs>(() => read());

  // Sync across tabs so a speed change in Settings reflects in an open
  // home tab without a refresh.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setPrefs(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const persist = useCallback((next: MasteryHeroPrefs) => {
    const row = { ...next, updatedAt: Date.now() };
    write(row);
    setPrefs(row);
  }, []);

  const setRotateSpeed = useCallback(
    (ms: number) => {
      persist({ ...prefs, rotateSpeedMs: ms });
    },
    [prefs, persist],
  );

  const setRoadmapOrder = useCallback(
    (order: number[] | null) => {
      persist({ ...prefs, roadmapOrder: order });
    },
    [prefs, persist],
  );

  const resetRoadmap = useCallback(() => {
    persist({ ...prefs, roadmapOrder: null });
  }, [prefs, persist]);

  return { prefs, setRotateSpeed, setRoadmapOrder, resetRoadmap };
}